import { useState, useEffect, useCallback } from 'react';

const BASE = '/api';
const getToken = () => localStorage.getItem('ozon_token');

async function apiFetch(method, path, body) {
  const r = await fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || r.statusText);
  return d;
}

function fmtDt(str) {
  if (!str) return '—';
  return new Date(str).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const STATUS_MAP = {
  pending:  { label: 'Bekliyor',   color: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: 'Onaylandı',  color: 'bg-green-50 text-green-700 border-green-200' },
  rejected: { label: 'Reddedildi', color: 'bg-red-50 text-red-700 border-red-200' },
};

// SKU arama dropdown
function SkuSearch({ value, onChange }) {
  const [query,   setQuery]   = useState(value || '');
  const [results, setResults] = useState([]);
  const [open,    setOpen]    = useState(false);

  useEffect(() => {
    if (value && value !== query) setQuery(value);
  }, [value]); // eslint-disable-line

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      apiFetch('GET', `/supply-map/my-skus?search=${encodeURIComponent(query)}`)
        .then(r => setResults(r))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  function select(row) {
    setQuery(row.offer_id);
    setOpen(false);
    onChange(row.offer_id);
  }

  return (
    <div className="relative">
      <input
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); onChange(e.target.value); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Ozon offer_id ara..."
        className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-blue-400"
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
          {results.map(r => (
            <button key={r.offer_id} onMouseDown={() => select(r)}
              className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 border-b border-gray-50 last:border-0">
              <div className="font-mono text-gray-800">{r.offer_id}</div>
              {r.name && <div className="text-gray-400 truncate">{r.name}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Talep kartı
function RequestCard({ req, onAction }) {
  const [items,   setItems]   = useState(req.items || []);
  const [note,    setNote]    = useState('');
  const [saving,  setSaving]  = useState(false);
  const [acting,  setActing]  = useState(false);
  const [open,    setOpen]    = useState(req.status === 'pending');
  const [dirty,   setDirty]   = useState(false);

  const sm = STATUS_MAP[req.status] || { label: req.status, color: 'bg-gray-50 text-gray-600 border-gray-200' };

  function updateMapping(idx, mappedOfferId) {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, mapped_offer_id: mappedOfferId } : it));
    setDirty(true);
  }

  async function saveMapping() {
    setSaving(true);
    try {
      await apiFetch('PATCH', `/incoming-requests/${req.id}/items`, { items });
      setDirty(false);
    } catch (e) { alert('Hata: ' + e.message); }
    finally { setSaving(false); }
  }

  async function act(action) {
    if (dirty) { alert('Önce mapping kaydet'); return; }
    setActing(true);
    try {
      await apiFetch('POST', `/incoming-requests/${req.id}/${action}`, { note });
      onAction();
    } catch (e) { alert('Hata: ' + e.message); }
    finally { setActing(false); }
  }

  const mappingComplete = items.every(it => it.mapped_offer_id || it.offer_id);

  return (
    <div className={`bg-white border rounded-2xl shadow-sm overflow-hidden ${req.status === 'pending' ? 'border-amber-200' : 'border-gray-100'}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 cursor-pointer select-none" onClick={() => setOpen(o => !o)}>
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${sm.color}`}>{sm.label}</span>
          <span className="text-sm font-medium text-gray-800">#{req.id} · {req.source_name || 'Dış kaynak'}</span>
          <span className="text-xs text-gray-400">{items.length} ürün</span>
          {req.session_note && <span className="text-xs text-gray-400 hidden sm:block">— {req.session_note}</span>}
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span>{fmtDt(req.created_at)}</span>
          <span>{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {open && (
        <div className="border-t border-gray-100 px-4 py-4">
          {/* Ürün + mapping tablosu */}
          <div className="mb-4 overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="text-xs text-gray-500 font-semibold border-b border-gray-100">
                  <th className="text-left py-2 w-1/3">Gelen Offer ID</th>
                  <th className="text-left py-2 w-1/4">Ürün Adı</th>
                  <th className="text-center py-2 w-16">Adet</th>
                  <th className="text-left py-2">→ Ozon'daki Eşleşme</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-2 pr-3 font-mono text-xs text-gray-700">{item.offer_id}</td>
                    <td className="py-2 pr-3 text-xs text-gray-500 max-w-[160px] truncate">{item.name}</td>
                    <td className="py-2 text-center font-bold text-gray-900">{item.qty}</td>
                    <td className="py-2 pl-3">
                      {req.status === 'pending' ? (
                        <SkuSearch
                          value={item.mapped_offer_id || item.offer_id}
                          onChange={val => updateMapping(i, val)}
                        />
                      ) : (
                        <span className="font-mono text-xs text-blue-700">{item.mapped_offer_id || item.offer_id}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {req.status === 'pending' && (
            <>
              {dirty && (
                <div className="flex justify-end mb-3">
                  <button onClick={saveMapping} disabled={saving}
                    className="px-4 py-1.5 bg-gray-800 text-white rounded-xl text-xs font-medium hover:bg-gray-900 disabled:opacity-50">
                    {saving ? 'Kaydediliyor...' : '💾 Mapping Kaydet'}
                  </button>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                <input value={note} onChange={e => setNote(e.target.value)}
                  placeholder="Not ekle (isteğe bağlı)..."
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm flex-1 focus:outline-none focus:border-blue-400" />
                <div className="flex gap-2">
                  <button onClick={() => act('approve')} disabled={acting || !mappingComplete}
                    className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                    ✓ Onayla
                  </button>
                  <button onClick={() => act('reject')} disabled={acting}
                    className="px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600 disabled:opacity-50">
                    ✗ Reddet
                  </button>
                </div>
              </div>
              {!mappingComplete && (
                <p className="text-xs text-amber-600 mt-2">⚠️ Tüm ürünler eşleştirilmeden onaylanamaz</p>
              )}
            </>
          )}

          {req.status !== 'pending' && (
            <div className="text-xs text-gray-400">
              Yanıtlandı: {fmtDt(req.responded_at)}
              {req.note && <span className="ml-2 text-gray-600">· {req.note}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function IncomingRequests() {
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? `?status=${filter}` : '';
      const data = await apiFetch('GET', `/incoming-requests${params}`);
      setRequests(Array.isArray(data) ? data : []);
    } catch (e) { alert('Hata: ' + e.message); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Gelen Stok Talepleri</h1>
          <p className="text-sm text-gray-500 mt-0.5">Dışarıdan gelen stok düşüm talepleri — eşleştir ve onayla</p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 border border-gray-200 text-gray-600 px-3 py-1.5 rounded-xl text-sm font-medium disabled:opacity-50">
          <span className={loading ? 'animate-spin inline-block' : ''}>↻</span> Yenile
        </button>
      </div>

      <div className="flex gap-1 mb-5 border-b border-gray-200">
        {[['pending','Bekleyenler'],['approved','Onaylananlar'],['rejected','Reddedilenler'],['','Tümü']].map(([val, label]) => (
          <button key={val} onClick={() => setFilter(val)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors ${
              filter === val ? 'border-blue-600 text-blue-700 bg-blue-50' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}>
            {label}
            {val === 'pending' && pendingCount > 0 && (
              <span className="ml-1.5 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">Kayıt bulunamadı</div>
      ) : (
        <div className="flex flex-col gap-3">
          {requests.map(r => <RequestCard key={r.id} req={r} onAction={load} />)}
        </div>
      )}
    </div>
  );
}
